import React, { useEffect, useState } from 'react';
import {
  Root as SelectRoot,
  Trigger as SelectTrigger,
  Icon as SelectIcon,
  Content as SelectContent,
  Item as SelectItem,
  Group as SelectGroup,
  Viewport as SelectViewport,
  ItemText,
  ItemIndicator,
  Portal,
} from '@radix-ui/react-select';
import { Box, CircularProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Icon, { AvailableIconTypes } from '@ui/Icon';
import Typography from '@ui/Typography';
import MenuButton from '../components/MenuButton';

export interface SingleSelectItem {
  label: string;
  value: string;
  isFavorite?: boolean;
  description?: string;
  icon?: AvailableIconTypes;
  imgSrc?: string;
  disabled?: boolean;
}
interface SingleSelectProps<T> {
  ariaLabel: string;
  value: string;
  items: T[];
  onChange: (item: SingleSelectItem) => void;
  onSelectedItemClick?: (item: SingleSelectItem) => void;
  openLabel?: string;
  hideIconOnOpenLabel?: boolean;
  active?: boolean;
  loading?: boolean;
  disabled?: boolean;
  footer?: React.ReactNode;
  valuePrefix?: string;
  maxHeight?: string;
  placeholder?: string;
  placeholderIcon?: AvailableIconTypes;
  variant?: 'normal' | 'language';
  tabIndex?: number;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  triggerRef?: React.Ref<HTMLButtonElement>;
}

const SingleSelect = <T extends SingleSelectItem>(
  props: SingleSelectProps<T>
) => {
  const {
    ariaLabel,
    value,
    items,
    openLabel,
    onChange,
    onSelectedItemClick,
    hideIconOnOpenLabel = false,
    loading,
    disabled,
    footer,
    valuePrefix = '',
    maxHeight,
    placeholder,
    placeholderIcon,
    variant = 'normal',
    tabIndex,
    onFocus,
    triggerRef,
  } = props;
  const [open, setOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(
    items.find(i => i.label === value)
  );

  const { classes, cx } = useStyles({ maxHeight, variant });

  useEffect(() => {
    setCurrentItem(
      items.find(i => i.label === value) || items.find(i => i.value === value)
    );
  }, [value, items]);

  const baseLabel = currentItem?.label || value || placeholder || '';

  const triggerIcon = (() => {
    if (hideIconOnOpenLabel && open && openLabel) return undefined;
    if (!currentItem && placeholderIcon) return placeholderIcon;
    return currentItem?.icon;
  })();

  const triggerImgSrc = (() => {
    if (hideIconOnOpenLabel && open && openLabel) return undefined;
    return currentItem?.imgSrc;
  })();

  const triggerLabel =
    open && openLabel ? openLabel : `${valuePrefix ?? ''}${baseLabel}`;

  return (
    <SelectRoot
      open={open}
      onOpenChange={setOpen}
      value={currentItem ? currentItem.label : ''}
      onValueChange={(item: string) => {
        if (item) onChange(items.find(i => i.label === item) || items[0]);
      }}
      disabled={disabled}
    >
      <SelectTrigger asChild className={classes.trigger}>
        <MenuButton
          ref={triggerRef}
          tabIndex={tabIndex}
          onFocus={onFocus}
          ariaLabel={ariaLabel}
          label={triggerLabel}
          icon={triggerIcon}
          imgSrc={triggerImgSrc}
        >
          {loading && (
            <CircularProgress
              size={16}
              style={{ marginLeft: 'auto' }}
              classes={{ circle: cx({ [classes.disabled]: loading }) }}
            />
          )}
          <SelectIcon asChild>
            <Icon
              type={!open ? 'chevronDown' : 'chevronUp'}
              className={classes.icon}
            />
          </SelectIcon>
        </MenuButton>
      </SelectTrigger>
      <Portal>
        <SelectContent
          position="popper"
          sideOffset={2}
          className={classes.content}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setOpen(false);
            }
          }}
        >
          <SelectViewport>
            <SelectGroup>
              {items.map(item => (
                <SelectItem
                  className={classes.selectItem}
                  value={item.label}
                  textValue={item.label}
                  disabled={item.disabled}
                  key={`${item.label}-${item.value}`}
                  onPointerDown={e => {
                    // For already-selected items, prevent focus and call onSelectedItemClick after dropdown closes
                    if (
                      onSelectedItemClick &&
                      item.label === (currentItem?.label || value)
                    ) {
                      e.preventDefault();
                      // Use setTimeout to let dropdown close first, avoiding aria-hidden warning
                      setTimeout(() => {
                        onSelectedItemClick(item);
                      }, 50);
                    }
                  }}
                  data-e2e={item.value}
                >
                  <Box display="flex" alignItems="center">
                    {item.imgSrc && (
                      <img
                        src={item.imgSrc}
                        alt=""
                        style={{
                          width: '16px',
                          height: '16px',
                          marginRight: '8px',
                        }}
                      />
                    )}
                    {item.icon && (
                      <Box display="flex" mr={1}>
                        <Icon type={item.icon} />
                      </Box>
                    )}
                    <ItemText asChild>
                      <Box
                        style={{ display: 'block', justifyContent: 'center' }}
                      >
                        <Typography variant="pSm" className={classes.label}>
                          {item.label}
                        </Typography>
                        {item.description && (
                          <Typography
                            variant="pXs"
                            color="tertiary"
                            className={classes.description}
                          >
                            {item.description}
                          </Typography>
                        )}
                      </Box>
                    </ItemText>
                    {item.isFavorite && (
                      <Box display="flex" ml={1} alignItems="center">
                        <Icon
                          type="favoriteFilled"
                          className={classes.favorite}
                        />
                      </Box>
                    )}
                  </Box>
                  <ItemIndicator className={classes.icon}>
                    <Icon type="check" />
                  </ItemIndicator>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectViewport>
          {footer && footer}
        </SelectContent>
      </Portal>
    </SelectRoot>
  );
};

const useStyles = makeStyles<{
  maxHeight?: string;
  variant: 'normal' | 'language';
}>()((theme, { maxHeight, variant }) => ({
  trigger: {
    ...(variant === 'language' && {
      '&[data-state="open"]': {
        outline: `1px solid ${palette.mint_300}`,
      },
    }),
  },
  selectItem: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.common.white,
    padding: theme.spacing(1),
    margin: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    '&[data-highlighted="true"]': {
      backgroundColor: palette.gray_700,
      outline: `1px solid ${palette.mint_200}`,
    },
    '&:not([data-disabled=""])': {
      backgroundColor: palette.gray_900,
      '&:hover': {
        backgroundColor: palette.gray_700,
        outline: 'none',
        cursor: 'pointer',
      },
      '&:focus-visible:not(&:hover)': {
        outline: `1px solid ${palette.mint_200}`,
        backgroundColor: palette.gray_700,
      },
    },
  },
  label: {
    color: theme.palette.common.white,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  favorite: { color: palette.punch_400, marginRight: '4px' },
  icon: {
    color: 'inherit',
    marginLeft: 'auto',
    marginRight: 0,
    height: theme.spacing(2),
    width: theme.spacing(2),
    boxSizing: 'content-box',
  },
  content: {
    width: 'var(--radix-select-trigger-width)',
    maxHeight: maxHeight || 'var(--radix-select-content-available-height)',
    overflowY: 'auto',
    zIndex: theme.zIndex.tooltip,
    backgroundColor: palette.gray_900,
    borderRadius: theme.spacing(0.5),
    boxShadow: theme.shadows[2],
    fontSize: '14px',
  },
  disabled: { color: palette.gray_400 },
  description: { paddingTop: theme.spacing(0.25) },
  prefix: { opacity: 0.7 },
}));

export default SingleSelect;
